import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography,
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TASK_PRIORITY_LABELS, TASK_VERIFICATION_LABELS } from '@/constants/task';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import * as tasksApi from '@/services/api/tasks';
import type { DepartmentTreeNode } from '@/types/department';
import type {
  CreateTaskBatchPayload,
  TaskDetail,
  TaskListItem,
  TaskPriority,
  TaskVerificationStatus,
} from '@/types/task';
import { flattenDepartmentTree, toParentTreeSelectData } from '@/utils/department-tree';
import { formatDateTime } from '@/utils/format-date';

const { RangePicker } = DatePicker;

interface FilterValues {
  keyword?: string;
  departmentId?: string;
  ownerId?: string;
  completionState?: 'COMPLETED' | 'INCOMPLETE';
  dueRange?: [Dayjs, Dayjs];
}

interface CreateValues {
  title: string;
  content: string;
  ownerIds: string[];
  dueAt: Dayjs;
  priority: TaskPriority;
}

const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

function verificationTag(status: TaskVerificationStatus, taskStatus: TaskListItem['status']) {
  if (taskStatus === 'CANCELLED') {
    return <Tag>已取消</Tag>;
  }
  const colorMap: Record<TaskVerificationStatus, string> = {
    NOT_SUBMITTED: 'orange',
    PENDING_REVIEW: 'blue',
    VERIFIED_COMPLETED: 'green',
    VERIFIED_INCOMPLETE: 'red',
  };
  return <Tag color={colorMap[status]}>{TASK_VERIFICATION_LABELS[status]}</Tag>;
}

function textOrDash(value: string | null | undefined) {
  return value?.trim() || '—';
}

export function TaskManagementPage() {
  const { message, modal } = App.useApp();
  const [filterForm] = Form.useForm<FilterValues>();
  const [createForm] = Form.useForm<CreateValues>();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterValues>({});
  const [departments, setDepartments] = useState<DepartmentTreeNode[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<TaskDetail | null>(null);

  const flatDepartments = useMemo(() => flattenDepartmentTree(departments), [departments]);
  const departmentTreeData = useMemo(() => toParentTreeSelectData(departments), [departments]);
  const people = useMemo(() => {
    const seen = new Set<string>();
    return flatDepartments.flatMap((department) =>
      department.users
        .filter((user) => user.role !== 'ADMIN' && !seen.has(user.id))
        .map((user) => {
          seen.add(user.id);
          return {
            value: user.id,
            label: `${user.name}（${department.name}）`,
            departmentName: department.name,
          };
        }),
    );
  }, [flatDepartments]);
  const groupedPeople = useMemo(
    () =>
      flatDepartments
        .map((department) => ({
          label: department.name,
          options: department.users
            .filter((user) => user.role !== 'ADMIN')
            .map((user) => ({ value: user.id, label: `${user.name}（${user.phone}）` })),
        }))
        .filter((group) => group.options.length > 0),
    [flatDepartments],
  );

  const loadDepartments = useCallback(async () => {
    try {
      setDepartments(await departmentsApi.fetchDepartmentTree());
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载部门人员失败';
      message.error(text);
    }
  }, [message]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await tasksApi.fetchTasks({
        page,
        pageSize,
        keyword: filters.keyword?.trim() || undefined,
        departmentId: filters.departmentId,
        ownerId: filters.ownerId,
        completionState: filters.completionState,
        dueAfter: filters.dueRange?.[0]?.startOf('day').toISOString(),
        dueBefore: filters.dueRange?.[1]?.endOf('day').toISOString(),
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载任务失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [filters, message, page, pageSize]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      setDetail(await tasksApi.fetchTask(id));
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载任务详情失败';
      message.error(text);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async (values: CreateValues) => {
    setCreating(true);
    try {
      const payload: CreateTaskBatchPayload = {
        title: values.title.trim(),
        content: values.content.trim(),
        ownerIds: values.ownerIds,
        dueAt: values.dueAt.toISOString(),
        priority: values.priority,
      };
      const result = await tasksApi.createTaskBatch(payload);
      message.success(`已向 ${result.total} 人派发任务`);
      setCreateOpen(false);
      createForm.resetFields();
      setPage(1);
      await loadTasks();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '任务派发失败';
      message.error(text);
    } finally {
      setCreating(false);
    }
  };

  const verify = (result: 'COMPLETED' | 'INCOMPLETE') => {
    if (!detail) return;
    let note = '';
    modal.confirm({
      title: result === 'COMPLETED' ? '确认任务已完成' : '确认任务未完成',
      content: (
        <div style={{ marginTop: 16 }}>
          <Typography.Paragraph type="secondary">
            {result === 'COMPLETED'
              ? '确认后该任务将计入已完成。'
              : '请填写实地核验情况，任务将退回接收人继续办理。'}
          </Typography.Paragraph>
          <Input.TextArea
            rows={4}
            maxLength={500}
            showCount
            placeholder={result === 'COMPLETED' ? '核验说明（选填）' : '请输入未完成原因'}
            onChange={(event) => {
              note = event.target.value;
            }}
          />
        </div>
      ),
      okText: result === 'COMPLETED' ? '确认完成' : '确认未完成',
      okType: result === 'COMPLETED' ? 'primary' : 'danger',
      cancelText: '取消',
      onOk: async () => {
        if (result === 'INCOMPLETE' && !note.trim()) {
          message.warning('请填写未完成原因');
          return Promise.reject(new Error('verification note is required'));
        }
        const updated = await tasksApi.verifyTask(detail.id, {
          result,
          note: note.trim() || undefined,
        });
        setDetail(updated);
        message.success(result === 'COMPLETED' ? '已确认完成' : '已退回继续办理');
        await loadTasks();
      },
    });
  };

  const columns: ColumnsType<TaskListItem> = [
    {
      title: '任务',
      dataIndex: 'title',
      width: 240,
      render: (value: string, record) => (
        <Space direction="vertical" size={2}>
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => void openDetail(record.id)}>
            {value}
          </Button>
          {record.project?.name ? <Typography.Text type="secondary">{record.project.name}</Typography.Text> : null}
        </Space>
      ),
    },
    {
      title: '接收人',
      key: 'owner',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.owner?.name ?? '—'}</div>
          <Typography.Text type="secondary">{record.owner?.department?.name ?? '—'}</Typography.Text>
        </div>
      ),
    },
    {
      title: '截止日期',
      dataIndex: 'dueAt',
      width: 170,
      render: (value: string, record) => (
        <Space>
          <span>{formatDateTime(value)}</span>
          {record.overdue ? <Tag color="red">逾期</Tag> : null}
        </Space>
      ),
    },
    {
      title: '当前进展',
      dataIndex: 'progressContent',
      ellipsis: true,
      render: textOrDash,
    },
    {
      title: '状态',
      key: 'verificationStatus',
      width: 120,
      render: (_, record) => verificationTag(record.verificationStatus, record.status),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 90,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => void openDetail(record.id)}>
          查看
        </Button>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (count) => `共 ${count} 条`,
    onChange: (nextPage, nextPageSize) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
  };

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>任务管理</Typography.Title>
      <Typography.Paragraph type="secondary">
        向部门人员逐人派发任务，查看办理进展，并在实地核验后确认完成情况。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Form<FilterValues>
          form={filterForm}
          layout="inline"
          onFinish={(values) => {
            setFilters(values);
            setPage(1);
          }}
        >
          <Form.Item name="keyword" label="任务">
            <Input allowClear placeholder="任务名称" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="departmentId" label="部门">
            <TreeSelect
              allowClear
              treeDefaultExpandAll
              treeData={departmentTreeData}
              placeholder="全部部门"
              style={{ width: 190 }}
            />
          </Form.Item>
          <Form.Item name="ownerId" label="人员">
            <Select allowClear showSearch optionFilterProp="label" options={people} placeholder="全部人员" style={{ width: 190 }} />
          </Form.Item>
          <Form.Item name="completionState" label="完成情况">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 130 }}
              options={[
                { value: 'COMPLETED', label: '已完成' },
                { value: 'INCOMPLETE', label: '未完成' },
              ]}
            />
          </Form.Item>
          <Form.Item name="dueRange" label="截止日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
              <Button onClick={() => { filterForm.resetFields(); setFilters({}); setPage(1); }}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>下发任务</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<TaskListItem>
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 1050 }}
        />
      </Card>

      <Drawer title="下发任务" width={560} open={createOpen} onClose={() => setCreateOpen(false)} destroyOnClose>
        <Form<CreateValues>
          form={createForm}
          layout="vertical"
          initialValues={{ priority: 'MEDIUM' }}
          onFinish={(values) => void handleCreate(values)}
        >
          <Form.Item name="title" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }, { max: 128 }]}>
            <Input maxLength={128} placeholder="请输入任务名称" />
          </Form.Item>
          <Form.Item name="content" label="任务详情" rules={[{ required: true, message: '请输入任务详情' }]}>
            <Input.TextArea rows={6} maxLength={4000} showCount placeholder="请说明任务要求和验收标准" />
          </Form.Item>
          <Form.Item name="ownerIds" label="接收人员" rules={[{ required: true, message: '请选择接收人员' }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={groupedPeople}
              placeholder="按部门选择相关人员"
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item name="dueAt" label="截止日期" rules={[{ required: true, message: '请选择截止日期' }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} disabledDate={(date) => date.endOf('day').isBefore(dayjs())} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={creating}>确认下发</Button>
            <Button onClick={() => setCreateOpen(false)}>取消</Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer title="任务详情" width={620} open={detailOpen} loading={detailLoading} onClose={() => setDetailOpen(false)}>
        {detail ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="任务名称">{detail.title}</Descriptions.Item>
              <Descriptions.Item label="接收人">{detail.owner?.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="所属部门">{detail.owner?.department?.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="截止日期">{formatDateTime(detail.dueAt)}</Descriptions.Item>
              <Descriptions.Item label="状态">{verificationTag(detail.verificationStatus, detail.status)}</Descriptions.Item>
              <Descriptions.Item label="任务详情"><Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{detail.content}</Typography.Paragraph></Descriptions.Item>
              <Descriptions.Item label="当前进展"><Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textOrDash(detail.progressContent)}</Typography.Paragraph></Descriptions.Item>
              <Descriptions.Item label="任务总结"><Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textOrDash(detail.completionNote)}</Typography.Paragraph></Descriptions.Item>
              {detail.verificationNote ? <Descriptions.Item label="核验说明">{detail.verificationNote}</Descriptions.Item> : null}
              {detail.verifiedAt ? <Descriptions.Item label="核验时间">{formatDateTime(detail.verifiedAt)}</Descriptions.Item> : null}
            </Descriptions>
            {detail.verificationStatus === 'PENDING_REVIEW' ? (
              <Space>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => verify('COMPLETED')}>确认完成</Button>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => verify('INCOMPLETE')}>确认未完成</Button>
              </Space>
            ) : null}
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
